import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { MapPin, ArrowLeft, ChevronLeft, ChevronRight, Star, ChevronRight as NavArrow, ChevronDown, X, Phone, MessageCircle, Navigation, Home, BadgeCheck, Images as ImageIcon, Bandage, Sprout } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Centre, CentreTimingDay, CentreSlot, CentreTherapist } from '@/api/centresApi';
import { centresApi } from '@/api/centresApi';
import { allCentreReviews, calcRating, firstPartyReviews, getDistanceKm } from '@/utils/centreStats';
import { PatientReviews, REVIEWS_ANCHOR } from '@/components/reviews/PatientReviews';
import { clampDescription } from '@/seo/meta';
import {
  centrePath,
  cityPath,
  parseCentreId,
  resolveCentreLocality,
  sectorPath,
} from '@/utils/seoPaths';
import { readSavedLocation } from '@/utils/savedLocation';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { galleryImages, resolveDefaultImages } from '@/utils/centreImages';
import { useCatalog } from '@/context/CatalogContext';
import { Seo } from '@/seo/Seo';
import { breadcrumbSchema, localBusinessSchema } from '@/seo/schemas';

// ── Timings / contact helpers ──────────────────────────────────────────────────
const DAY_ORDER: CentreTimingDay['day'][] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<CentreTimingDay['day'], string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const COVERAGE_LABELS: Record<NonNullable<Centre['home_visit_coverage']>, string> = {
  full_city: 'Across the city',
  selected_areas: 'Selected areas',
  none: 'Not available',
};

/** Normalise a day's availability to a list of slots, tolerating the legacy
 *  single open/close shape. */
function daySlots(d: CentreTimingDay): CentreSlot[] {
  if (d.slots && d.slots.length) return d.slots;
  if (d.open && d.close) return [{ open: d.open, close: d.close }];
  return [];
}

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** "09:00" → "9:00 am". */
function fmtTime(hhmm: string): string {
  const [hStr, m] = hhmm.split(':');
  const h = Number(hStr);
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${period}`;
}

/** Index into DAY_ORDER for *today* using the browser's local time. */
function todayKey(): CentreTimingDay['day'] {
  return DAY_ORDER[(new Date().getDay() + 6) % 7]; // getDay(): 0 = Sunday
}

type OpenStatus = { open: boolean; closesAt?: string; todayClosed?: boolean };

function getOpenStatus(timings?: CentreTimingDay[]): OpenStatus | null {
  if (!timings || timings.length === 0) return null;
  const today = timings.find((t) => t.day === todayKey());
  if (!today || today.closed) return { open: false, todayClosed: true };
  const slots = daySlots(today);
  if (slots.length === 0) return { open: false, todayClosed: true };
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  for (const s of slots) {
    if (cur >= minutesOf(s.open) && cur < minutesOf(s.close)) {
      return { open: true, closesAt: s.close };
    }
  }
  return { open: false };
}

function directionsUrl(centre: Centre): string {
  if (centre.location?.lat != null && centre.location?.lng != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${centre.location.lat},${centre.location.lng}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(centre.address)}`;
}

// ── At-a-glance info panel (right column, under the carousel) ────────────────────
function InfoPanel({ centre }: { centre: Centre }) {
  const requireAuth = useRequireAuth();
  const status = getOpenStatus(centre.timings);
  const phone = centre.phone?.replace(/\D/g, '');
  const wa = (centre.whatsapp || centre.phone)?.replace(/\D/g, '');
  const coverage = centre.home_visit_coverage;
  const offersHomeVisit =
    (coverage && coverage !== 'none') || centre.services.some((s) => s.home_visit);

  return (
    <div className="rounded-2xl border bg-card p-5 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        At a glance
      </h2>

      {status && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <span
            className={`inline-block h-2 w-2 rounded-full ${status.open ? 'bg-green-500' : 'bg-red-500'}`}
            aria-hidden="true"
          />
          {status.open ? (
            <span className="font-medium text-foreground">
              Open now{status.closesAt ? ` · closes ${fmtTime(status.closesAt)}` : ''}
            </span>
          ) : (
            <span className="font-medium text-muted-foreground">
              {status.todayClosed ? 'Closed today' : 'Closed now'}
            </span>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2">
        {/* Contact actions require login — browsing stays open (PDF p19-20) */}
        {phone && (
          <Button
            className="w-full justify-start gap-3 px-4"
            onClick={() => requireAuth(() => { window.location.href = `tel:+91${phone}`; })}
          >
            <Phone className="h-4 w-4 shrink-0" /> Call clinic
          </Button>
        )}
        {wa && (
          <Button
            variant="outline"
            className="w-full justify-start gap-3 px-4"
            onClick={() => requireAuth(() => window.open(`https://wa.me/91${wa}`, '_blank', 'noopener,noreferrer'))}
          >
            <MessageCircle className="h-4 w-4 shrink-0" /> WhatsApp
          </Button>
        )}
        {centre.location?.lat != null && centre.location?.lng != null && (
          <Button
            variant="outline"
            className="w-full justify-start gap-3 px-4"
            onClick={() => requireAuth(() => window.open(directionsUrl(centre), '_blank', 'noopener,noreferrer'))}
          >
            <Navigation className="h-4 w-4 shrink-0" /> Get directions
          </Button>
        )}
      </div>

      {offersHomeVisit && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-primary/5 p-3 text-sm">
          <Home className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            {/* Home-visit surcharge is intentionally NOT shown here — it's an
                internal figure applied at booking and rolled into the final
                service cost on the booking page (client note, PDF p20). */}
            <p className="font-medium text-foreground">Home visit available</p>
            {coverage && coverage !== 'none' && (
              <p className="text-xs text-muted-foreground">
                {COVERAGE_LABELS[coverage]}
                {coverage === 'selected_areas' && centre.home_visit_areas
                  ? `: ${centre.home_visit_areas}`
                  : ''}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Only rendered when the clinic filled it in: an empty "Also practises at"
          heading would read as a gap rather than an omission. */}
      {centre.hospital_affiliation?.trim() && (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Also practises at
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground">
            {centre.hospital_affiliation}
          </p>
        </div>
      )}

      {centre.languages && centre.languages.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Languages spoken
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {centre.languages.map((l) => (
              <Badge key={l} variant="secondary">{l}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Collapsible detail section (reuses the service accordion look) ───────────────
function Section({
  title,
  subtitle,
  defaultOpen = false,
  children,
}: {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 bg-muted/30 px-4 py-3 text-left hover:bg-muted/50"
        aria-expanded={open}
      >
        <span className="font-semibold text-foreground">
          {title}
          {subtitle ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">{subtitle}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="border-t border-border bg-background px-4 py-3">{children}</div>}
    </div>
  );
}

function OpeningHours({ timings }: { timings: CentreTimingDay[] }) {
  const today = todayKey();
  const byDay = new Map(timings.map((t) => [t.day, t]));
  return (
    <ul className="flex flex-col gap-1.5 text-sm">
      {DAY_ORDER.map((day) => {
        const t = byDay.get(day);
        const slots = t ? daySlots(t) : [];
        const closed = !t || t.closed || slots.length === 0;
        const isToday = day === today;
        return (
          <li
            key={day}
            className={`flex items-center justify-between gap-4 ${isToday ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
          >
            <span>{DAY_LABELS[day]}{isToday ? ' · Today' : ''}</span>
            <span className="text-right">
              {closed
                ? 'Closed'
                : slots.map((s) => `${fmtTime(s.open)} – ${fmtTime(s.close)}`).join(', ')}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function TeamSection({ therapists }: { therapists: CentreTherapist[] }) {
  return (
    <ul className="flex flex-col gap-3">
      {therapists.map((t, i) => {
        const meta = [
          t.qualification,
          t.specialisation,
          t.affiliation,
          typeof t.experience === 'number'
            ? `${t.experience} yr${t.experience === 1 ? '' : 's'} exp`
            : null,
        ].filter(Boolean);
        return (
          <li key={i} className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
              {t.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t.name}</p>
              {meta.length > 0 && (
                <p className="text-xs text-muted-foreground">{meta.join(' · ')}</p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ── Clinic hero: cover photo with overlaid identity + trust chips ───────────────
function CentreHero({
  centre,
  rating,
  reviewCount,
  distance,
  onReviews,
}: {
  centre: Centre;
  rating: number | null;
  reviewCount: number;
  distance: number | null;
  onReviews: () => void;
}) {
  const { default_centre_image } = useSiteConfig();
  const defaults = resolveDefaultImages(default_centre_image);
  // Padded to a full strip, so a clinic with one photo still reads as a gallery.
  const images = galleryImages(centre, defaults);
  const fallbackImage = defaults[0];
  const stripRef = useRef<HTMLDivElement>(null);
  const [lightboxAt, setLightboxAt] = useState<number | null>(null);

  // Scrolls exactly one tile, measured off the rendered tile so the step stays
  // correct across breakpoints instead of hardcoding a width.
  const scrollByTile = (direction: 1 | -1) => {
    const strip = stripRef.current;
    const tile = strip?.firstElementChild as HTMLElement | undefined;
    if (!strip || !tile) return;
    const gap = parseFloat(getComputedStyle(strip).columnGap || '0') || 0;
    strip.scrollBy({ left: direction * (tile.offsetWidth + gap), behavior: 'smooth' });
  };
  const status = getOpenStatus(centre.timings);
  const offersHomeVisit =
    (centre.home_visit_coverage && centre.home_visit_coverage !== 'none') ||
    centre.services.some((s) => s.home_visit);

  return (
    <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
      {/* Photo strip - 3:2 tiles, two visible on desktop with the next peeking so
          the gallery reads as browsable. Tiles open the lightbox. */}
      <div className="relative">
        <div
          ref={stripRef}
          className="flex snap-x snap-mandatory gap-1.5 overflow-x-auto scroll-smooth [scrollbar-width:none] motion-reduce:scroll-auto [&::-webkit-scrollbar]:hidden"
        >
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setLightboxAt(i)}
              aria-label={`View photo ${i + 1} of ${images.length}`}
              className="group relative aspect-[3/2] w-[86%] shrink-0 snap-start overflow-hidden bg-muted/40 sm:w-[47%]"
            >
              <img
                src={src}
                alt={`${centre.name} photo ${i + 1}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                onError={(e) => {
                  const el = e.currentTarget;
                  if (fallbackImage && el.src !== fallbackImage) el.src = fallbackImage;
                }}
              />
            </button>
          ))}
        </div>

        {/* Strip controls */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => scrollByTile(-1)}
              aria-label="Previous photos"
              className="absolute left-3 top-1/2 hidden size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-foreground shadow-md transition hover:scale-105 sm:flex"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              onClick={() => scrollByTile(1)}
              aria-label="More photos"
              className="absolute right-3 top-1/2 hidden size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-foreground shadow-md transition hover:scale-105 sm:flex"
            >
              <ChevronRight className="size-5" />
            </button>
          </>
        )}

        <button
          onClick={() => setLightboxAt(0)}
          className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-lg bg-white/95 px-3 py-2 text-sm font-semibold text-foreground shadow-md backdrop-blur transition hover:scale-[1.03]"
        >
          <ImageIcon className="size-4" />
          View all photos
        </button>
      </div>

      {/* Identity - sits below the strip now that the cover is no longer a single
          full-bleed hero to overlay text on. */}
      <div className="flex items-start justify-between gap-4 px-5 pt-4 sm:px-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight text-foreground sm:text-3xl">
            {centre.name}
          </h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="size-4 shrink-0" />
            <span>
              {centre.address}
              {distance != null ? ` \u00b7 ${distance.toFixed(1)} km away` : ''}
            </span>
          </p>
        </div>
        <button
          onClick={onReviews}
          title="View all reviews"
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold text-foreground transition-transform hover:scale-105"
        >
          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
          {rating !== null ? rating.toFixed(1) : 'New'}
          {reviewCount > 0 && <span className="font-normal text-muted-foreground">({reviewCount})</span>}
        </button>
      </div>

      {lightboxAt !== null && (
        <PhotoLightbox
          images={images}
          startAt={lightboxAt}
          centreName={centre.name}
          onClose={() => setLightboxAt(null)}
        />
      )}

      {/* Trust chips */}
      <div className="flex flex-wrap items-center gap-2 px-5 py-3.5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-primary">
          <BadgeCheck className="size-3.5" /> Verified
        </span>
        {status && (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
              status.open ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
            }`}
          >
            <span className={`size-1.5 rounded-full ${status.open ? 'bg-green-500' : 'bg-red-400'}`} />
            {status.open
              ? `Open now${status.closesAt ? ` · closes ${fmtTime(status.closesAt)}` : ''}`
              : status.todayClosed
                ? 'Closed today'
                : 'Closed now'}
          </span>
        )}
        {offersHomeVisit && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-foreground">
            <Home className="size-3.5 text-primary" /> Home visit
          </span>
        )}
        {centre.languages && centre.languages.length > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            {centre.languages.slice(0, 3).join(', ')}
          </span>
        )}
      </div>
    </div>
  );
}

// Full-screen photo viewer opened from the strip or "View all photos".
function PhotoLightbox({
  images,
  startAt,
  centreName,
  onClose,
}: {
  images: string[];
  startAt: number;
  centreName: string;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(startAt);
  const go = (direction: 1 | -1) =>
    setIndex((i) => (i + direction + images.length) % images.length);

  // Esc closes, arrows page through - a viewer that traps focus but ignores the
  // keyboard is worse than no viewer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${centreName} photos`}
    >
      <button
        onClick={onClose}
        aria-label="Close photos"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
      >
        <X className="size-5" />
      </button>

      <img
        src={images[index]}
        alt={`${centreName} photo ${index + 1}`}
        className="max-h-[85vh] w-auto max-w-full rounded-lg object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous photo"
            className="absolute left-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <ChevronLeft className="size-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next photo"
            className="absolute right-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <ChevronRight className="size-6" />
          </button>
          <span className="absolute bottom-6 rounded-full bg-black/60 px-3 py-1 text-sm font-medium text-white">
            {index + 1} / {images.length}
          </span>
        </>
      )}
    </div>
  );
}

// ── Service browser: drill path → category → service, filtered to this centre ──
function ServiceRow({ centreId, svc, idx }: { centreId: string; svc: Centre['services'][number]; idx: number }) {
  const hasPrice = typeof svc.price === 'number' && svc.price > 0;
  const duration = svc.duration;
  return (
    <Link
      to={`/centres/${centreId}/service/${idx}`}
      className="flex items-center justify-between gap-4 px-1 py-3 text-sm transition-colors hover:text-primary"
    >
      <span className="font-medium text-foreground">{svc.name}</span>
      <span className="flex shrink-0 items-center gap-2">
        {hasPrice ? (
          <Badge variant="outline">
            ₹{svc.price}
            {typeof duration === 'number' && duration > 0 ? (
              <span className="ml-1 text-muted-foreground">· {duration} min</span>
            ) : null}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">Based on consultation</span>
        )}
        <NavArrow className="h-4 w-4 text-muted-foreground" />
      </span>
    </Link>
  );
}

function CentreServiceBrowser({ centre, centreId }: { centre: Centre; centreId: string }) {
  const { catalog } = useCatalog();
  // Collapsed by default — the user opens a path, then a category, to drill in.
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [openCat, setOpenCat] = useState<string | null>(null);

  // First centre-service index per slug, so a service links to its detail page.
  const slugToIndex = useMemo(() => {
    const m = new Map<string, number>();
    centre.services.forEach((s, i) => {
      if (s.slug && !m.has(s.slug)) m.set(s.slug, i);
    });
    return m;
  }, [centre.services]);

  const offered = useMemo(() => new Set(slugToIndex.keys()), [slugToIndex]);

  // Path → categories that contain at least one service this centre offers.
  const tree = useMemo(() => {
    return (catalog?.paths ?? [])
      .map((path) => {
        const cats = path.categories
          .map((cat) => {
            const slugs = cat.services.filter((slug) => offered.has(slug));
            return slugs.length ? { cat, slugs } : null;
          })
          .filter((c): c is { cat: typeof path.categories[number]; slugs: string[] } => c !== null);
        return cats.length ? { path, cats } : null;
      })
      .filter((p): p is { path: NonNullable<typeof catalog>['paths'][number]; cats: { cat: NonNullable<typeof catalog>['paths'][number]['categories'][number]; slugs: string[] }[] } => p !== null);
  }, [catalog, offered]);

  // Services not represented anywhere in the path tree (custom / off-catalog).
  const inTree = useMemo(() => {
    const s = new Set<string>();
    tree.forEach(({ cats }) => cats.forEach(({ slugs }) => slugs.forEach((sl) => s.add(sl))));
    return s;
  }, [tree]);
  const additional = centre.services
    .map((svc, idx) => ({ svc, idx }))
    .filter(({ svc }) => !svc.slug || !inTree.has(svc.slug));

  return (
    <div>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Services Offered
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Pick a path, then a category, to find the exact service you need.
      </p>

      {/* Same tile treatment as the home page, so someone arriving here from
          there recognises the two paths rather than re-reading them. These
          expand in place, though, because the services belong to this clinic —
          hence a chevron where the home page has an arrow. */}
      <div className="mt-4 flex flex-col gap-4">
        {tree.map(({ path, cats }) => {
          const pathOpen = openPath === path.id;
          const count = cats.reduce((n, c) => n + c.slugs.length, 0);
          const featured = path.id === 'A';
          const chips = cats.slice(0, 3).map(({ cat }) => cat.label);
          const moreCats = cats.length - chips.length;
          return (
            <div
              key={path.id}
              className={cn(
                'overflow-hidden rounded-2xl transition-shadow',
                featured
                  ? 'border-[1.5px] border-[var(--sc-teal-mid)] bg-gradient-to-br from-secondary to-card'
                  : 'border border-border bg-card',
                pathOpen
                  ? 'shadow-[0_6px_20px_rgba(15,110,86,0.10)]'
                  : 'hover:shadow-[0_6px_20px_rgba(15,110,86,0.08)]',
              )}
            >
              <button
                type="button"
                onClick={() => setOpenPath(pathOpen ? null : path.id)}
                className="group w-full p-5 text-left sm:p-6"
                aria-expanded={pathOpen}
              >
                <div className="mb-2.5 flex items-start justify-between gap-3">
                  <span
                    className={cn(
                      'flex size-10 shrink-0 items-center justify-center rounded-xl',
                      featured && 'bg-secondary text-primary',
                    )}
                    style={
                      featured
                        ? undefined
                        : { background: 'var(--sc-amber-light)', color: 'var(--sc-amber)' }
                    }
                  >
                    {featured ? <Bandage className="size-5" /> : <Sprout className="size-5" />}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {count} {count === 1 ? 'service' : 'services'}
                    </span>
                    <ChevronDown
                      className={cn(
                        'size-5 shrink-0 text-primary transition-transform',
                        pathOpen && 'rotate-180',
                      )}
                    />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-foreground">{path.label}</h3>
                {path.blurb && (
                  <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                    {path.blurb}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {chips.map((label) => (
                    <span
                      key={label}
                      className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {label}
                    </span>
                  ))}
                  {moreCats > 0 && (
                    <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                      +{moreCats} more
                    </span>
                  )}
                </div>
              </button>

              {pathOpen && (
                <div className="divide-y divide-border border-t border-border bg-card">
                  {cats.map(({ cat, slugs }) => {
                    const catKey = `${path.id}:${cat.id}`;
                    const catOpen = openCat === catKey;
                    return (
                      <div key={cat.id}>
                        <button
                          type="button"
                          onClick={() => setOpenCat(catOpen ? null : catKey)}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/30"
                          aria-expanded={catOpen}
                        >
                          <span
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg text-base"
                            style={{ backgroundColor: cat.color }}
                            aria-hidden="true"
                          >
                            {cat.icon}
                          </span>
                          <span className="flex-1 text-sm font-medium text-foreground">{cat.label}</span>
                          <span className="text-xs text-muted-foreground">{slugs.length}</span>
                          <ChevronDown className={`size-4 shrink-0 text-muted-foreground transition-transform ${catOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {catOpen && (
                          <ul className="flex flex-col divide-y divide-border bg-background px-3 pb-1">
                            {slugs.map((slug) => {
                              const idx = slugToIndex.get(slug)!;
                              return <li key={slug}><ServiceRow centreId={centreId} svc={centre.services[idx]} idx={idx} /></li>;
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {additional.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-border">
            <div className="bg-muted/30 px-4 py-3 font-semibold text-foreground">
              Additional services
            </div>
            <ul className="flex flex-col divide-y divide-border px-3 pb-1">
              {additional.map(({ svc, idx }) => (
                <li key={`${svc.name}-${idx}`}>
                  <ServiceRow centreId={centreId} svc={svc} idx={idx} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {tree.length === 0 && additional.length === 0 && (
          <p className="text-sm text-muted-foreground">No services listed yet.</p>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CentreDetail() {
  // Mounted at /centres/:id and at the nested SEO URL
  // /physiotherapy-in-<city>/<sub-region>/:centreId.
  const { id: idParam, centreId } = useParams<{ id: string; centreId: string }>();
  const id = idParam ?? parseCentreId(centreId);
  const navigate = useNavigate();
  const [fetched, setFetched] = useState<{
    id: string;
    centre: Centre | null;
    error: boolean;
  } | null>(null);
  const isCurrent = !!id && fetched?.id === id;
  const centre = isCurrent ? fetched.centre : null;
  const error = isCurrent ? fetched.error : false;
  const loading = !isCurrent && !!id;
  const { default_centre_image } = useSiteConfig();
  const jumpToReviews = () =>
    document.getElementById(REVIEWS_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const location = useLocation();
  const routeState = location.state as { distance?: number; fromSearch?: string } | null;

  // Navigate forward to /centres with the previously-applied filters preserved
  // (passed via router state from the Centres listing page). Forward navigation
  // avoids getting stuck on intermediate Service-detail pages in the history stack.
  const handleBack = () => {
    const search = routeState?.fromSearch || window.sessionStorage.getItem('centresLastSearch') || '';
    navigate(`/centres${search}`);
  };
  // Router state when we came from a list; otherwise measure from the saved
  // location. Derived during render - there is nothing to store.
  const distance = useMemo(() => {
    if (routeState?.distance != null) return routeState.distance;
    const lat = centre?.location?.lat;
    const lng = centre?.location?.lng;
    if (lat == null || lng == null) return null;
    const saved = readSavedLocation();
    return saved ? getDistanceKm(saved.lat, saved.lng, lat, lng) : null;
  }, [centre, routeState]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    centresApi
      .detail(id)
      .then((res) => {
        if (!cancelled) setFetched({ id, centre: res.data.centre, error: false });
      })
      .catch(() => {
        if (!cancelled) setFetched({ id, centre: null, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Nested URL whose last segment carries no clinic id: nothing would ever load.
  if (!id) return <Navigate to="/centres" replace />;

  if (loading) {
    return (
      <div data-loading className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
        Loading centres...
      </div>
    );
  }

  if (error) {
    return (
      <div data-load-error className="mx-auto max-w-2xl px-4 py-20 text-center">
        <p className="text-lg font-medium text-destructive">Failed to load centres.</p>
        <Button variant="ghost" className="mt-4" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Centres
        </Button>
      </div>
    );
  }

  if (!centre) return null;

  const allReviews = allCentreReviews(centre);
  const rating = calcRating(allReviews);
  const reviewCount = allReviews.filter((r) => typeof r.rating === 'number').length;
  // Structured data may only claim ratings we collected ourselves.
  const schemaReviews = firstPartyReviews(allReviews);
  const schemaRating = calcRating(schemaReviews);
  const schemaCount = schemaReviews.filter((r) => typeof r.rating === 'number').length;
  const hasTimings = (centre.timings?.length ?? 0) > 0;
  const hasTeam = (centre.therapists?.length ?? 0) > 0;
  const hasNotes = typeof centre.notes === 'string' && centre.notes.trim().length > 0;
  const centreImages = [
    ...(centre.images ?? []),
    centre.image,
    centre.image2,
    centre.image3,
    centre.image4,
  ].filter((s): s is string => typeof s === 'string' && s.length > 0);
  // LocalBusiness requires `image`; clinics without photos use the site default,
  // which is the same picture the gallery shows visitors.
  const schemaImages = centreImages.length > 0 ? centreImages : resolveDefaultImages(default_centre_image).slice(0, 1);
  const ogImage = schemaImages.find((u) => /^https?:\/\//.test(u));
  const servicesList = centre.services.map((s) => s.name).slice(0, 3).join(', ');
  // One canonical per clinic: the locality URL when the clinic sits inside a
  // mapped sub-region, else the plain /centres/<id>. Both routes render this
  // page, so without this they would compete as duplicates.
  const locality = resolveCentreLocality(centre);
  const canonicalPath = locality
    ? centrePath(locality.city.slug, locality.sector.slug, centre.id, centre.name)
    : `/centres/${id}`;
  const centreUrl = `https://www.recovry.in${canonicalPath}`;
  // The legacy /centres/<id> form (and a stale name segment) redirect to the
  // canonical so Google consolidates onto one URL. Client-side because the
  // mapping needs clinic coordinates; the edge cannot know it.
  if (location.pathname !== canonicalPath) {
    return <Navigate to={{ pathname: canonicalPath, search: location.search }} state={routeState} replace />;
  }

  // Title budget is 60 chars: long clinic names drop the middle segment.
  const seoTitle =
    `${centre.name} | Physiotherapy Centre | Recovry`.length <= 60
      ? `${centre.name} | Physiotherapy Centre | Recovry`
      : `${centre.name} | Recovry`.length <= 60
        ? `${centre.name} | Recovry`
        : `${centre.name.slice(0, 47).trim()}… | Recovry`;
  const seoDescription = clampDescription(
    `${centre.name} - verified physiotherapy centre${locality ? ` in ${locality.sector.displayName}, ${locality.city.name}` : ''}. ${servicesList ? `Services: ${servicesList}.` : ''} Book online via Recovry.`,
  );

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalPath}
        ogImage={ogImage}
        schema={[
          // Mirrors the canonical URL, so the trail Google shows matches the
          // path and each level is a real, linkable page.
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Find a Centre', path: '/centres' },
            ...(locality
              ? [
                  { name: locality.city.name, path: cityPath(locality.city.slug) },
                  {
                    name: locality.sector.displayName,
                    path: sectorPath(locality.city.slug, locality.sector.slug),
                  },
                ]
              : []),
            { name: centre.name, path: canonicalPath },
          ]),
          localBusinessSchema({
            name: centre.name,
            description: seoDescription,
            url: centreUrl,
            image: schemaImages.length > 0 ? schemaImages : undefined,
            telephone: centre.phone,
            address: {
              streetAddress: centre.address,
              addressLocality: centre.address.split(',').slice(-2, -1)[0]?.trim() || 'India',
              addressCountry: 'IN',
            },
            geo: centre.location?.lat && centre.location?.lng
              ? { lat: centre.location.lat, lng: centre.location.lng }
              : undefined,
            aggregateRating:
              schemaRating !== null && schemaCount > 0
                ? { value: Math.round(schemaRating * 10) / 10, count: schemaCount }
                : undefined,
            reviews: schemaReviews
              .filter((r) => typeof r.rating === 'number')
              .slice(0, 10)
              .map((r) => ({ author: r.user || 'Verified patient', rating: r.rating as number, body: r.comment, date: r.date })),
          }),
        ]}
      />

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          All Centres
        </Button>

        {/* Hero - clinic photo with overlaid identity + trust chips. */}
        <CentreHero
          centre={centre}
          rating={rating}
          reviewCount={reviewCount}
          distance={distance}
          onReviews={jumpToReviews}
        />

        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
          {/* At-a-glance contact. order-1 on mobile so the call/Open-now actions
              sit right under the hero; a sticky sidebar on desktop. */}
          <div className="order-1 w-full lg:order-2 lg:w-80 lg:shrink-0 lg:sticky lg:top-24">
            <InfoPanel centre={centre} />
          </div>

          {/* Services + inline detail sections - kept on the page (good for trust
              + SEO) but collapsible to stay tidy. */}
          <div className="order-2 flex-1 lg:order-1">
            <div className="rounded-2xl border bg-card p-6 shadow-sm">
              <CentreServiceBrowser centre={centre} centreId={id!} />

              {(hasTimings || hasTeam || hasNotes) && (
                <div className="mt-6 flex flex-col gap-2">
                  {hasTimings && (
                    <Section title="Opening hours" defaultOpen>
                      <OpeningHours timings={centre.timings!} />
                    </Section>
                  )}
                  {hasTeam && (
                    <Section title="Our team" subtitle={`${centre.therapists!.length}`}>
                      <TeamSection therapists={centre.therapists!} />
                    </Section>
                  )}
                  {hasNotes && (
                    <Section title="About this clinic">
                      <p className="whitespace-pre-line text-sm text-muted-foreground">
                        {centre.notes}
                      </p>
                    </Section>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <PatientReviews reviews={allReviews} rating={rating} />
      </div>
    </>
  );
}
